import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { useServerPagination } from "../../../hooks/use-server-pagination";
import { useGet, usePost } from "../../../packages/hooks";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { useAuthStore } from "../../../store/useAuth";
import { Brand, Model } from "../../../types";
import { getAuthHeaders } from "../../../utils/auth-utils";

export interface UseModelsReturn {
  models: Model[];
  brands: Brand[];
  isLoading: boolean;
  totalItems: number;
  fetchModels: () => Promise<void>;
  fetchBrands: () => Promise<void>;
  handleCreateModel: (values: Partial<Model>) => Promise<any>;
  handleUpdateModel: (id: string, values: Partial<Model>) => Promise<any>;
  handleDeleteModel: (id: string) => Promise<boolean>;
  // Pagination properties
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (itemsPerPage: number) => void;
}

/**
 * Custom hook for model management using hooks from @hooks/ package
 * @param selectedBranchId - Optional selected branch ID for filtering
 * @returns Model state and management functions
 */
export const useModels = (
  selectedBranchId?: string | null
): UseModelsReturn => {
  const [models, setModels] = useState<Model[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Initialize server-side pagination
  const pagination = useServerPagination(totalItems);

  // Build query params with optional branch filter
  const queryParams = {
    ...pagination.getQueryParams,
    ...(selectedBranchId && { branchId: selectedBranchId }),
  };

  // Initialize hooks
  const modelsQuery = useGet<{
    models: Model[];
    pagination: { total: number };
  }>(API_ENDPOINTS.MODELS.LIST, {
    queryParams,
    onSuccess: (data) => {
      // Try multiple possible structures
      let modelList: Model[] = [];
      let totalCount = 0;

      if (data) {
        // Check for models array directly
        if (Array.isArray(data?.models)) {
          modelList = data.models;
          totalCount = data?.pagination?.total || modelList.length;
        }
        // Check for data.models structure
        else if (Array.isArray(data?.data?.models)) {
          modelList = data.data.models;
          totalCount = data?.data?.pagination?.total || modelList.length;
        }
        // Check if data itself is an array
        else if (Array.isArray(data)) {
          modelList = data;
          totalCount = data.length;
        }
        // Check for data.data array structure
        else if (Array.isArray(data?.data)) {
          modelList = data.data;
          totalCount = data.data.length;
        }
      }

      setModels(modelList);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      toast.error("Ошибка при загрузке моделей" + error);
    },
  });

  const brandsQuery = useGet<{
    brands: Brand[];
    pagination: { total: number };
  }>(API_ENDPOINTS.BRANDS.LIST, {
    queryParams: {
      page: 1,
      limit: 100, // Fetch maximum allowed brands for dropdown selection
      ...(selectedBranchId && { branchId: selectedBranchId }),
    },
    onSuccess: (data) => {
      // Try multiple possible structures
      let brandList: Brand[] = [];

      if (data) {
        let rawBrands: any[] = [];

        // Check for brands array directly
        if (Array.isArray(data?.brands)) {
          rawBrands = data.brands;
        }
        // Check for data.brands structure
        else if (Array.isArray(data?.data?.brands)) {
          rawBrands = data.data.brands;
        }
        // Check if data itself is an array
        else if (Array.isArray(data)) {
          rawBrands = data;
        }
        // Check for data.data array structure
        else if (Array.isArray(data?.data)) {
          rawBrands = data.data;
        }

        // Map the raw data to Brand objects
        brandList = rawBrands.map((b: any) => ({
          id: b._id || b.id,
          name: b.name,
          logo: b.logo,
          description: b.description,
          createdAt: b.createdAt,
          updatedAt: b.updatedAt,
        }));
      }

      setBrands(brandList);
    },
    onError: (error) => {
      toast.error("Ошибка при загрузке брендов" + error);
    },
  });

  const createMutation = usePost<Model>(API_ENDPOINTS.MODELS.LIST, {
    onSuccess: () => {
      toast.success("Модель успешно создана");
      modelsQuery.refetch();
    },
    onError: () => {
      toast.error("Ошибка при создании модели");
    },
  });

  const fetchModels = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await modelsQuery.refetch();
    } catch (error) {
      toast.error("Ошибка при загрузке моделей");
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to refetch when pagination or selected branch changes
  useEffect(() => {
    modelsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.itemsPerPage, selectedBranchId]);

  const fetchBrands = async (): Promise<void> => {
    try {
      await brandsQuery.refetch();
    } catch (error) {
      toast.error("Ошибка при загрузке брендов");
    }
  };

  // TODO: Improve error handling and add proper response types
  const handleCreateModel = async (values: Partial<Model>): Promise<any> => {
    try {
      setIsLoading(true);
      const response = await createMutation.mutateAsync({
        name: values.name || "",
        brandId: values.brandId || "",
        ...(selectedBranchId && { branchId: selectedBranchId }),
      });
      return response;
    } catch (error) {
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  // TODO: Improve error handling and add proper response types
  const handleUpdateModel = async (
    id: string,
    values: Partial<Model>
  ): Promise<any> => {
    try {
      setIsLoading(true);

      // Construct endpoint with branchId query parameter
      let endpoint = API_ENDPOINTS.MODELS.BY_ID(id);
      if (selectedBranchId) {
        endpoint += `?branchId=${selectedBranchId}`;
      }
      const url = buildApiUrl(endpoint);
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      // Prepare request body
      const requestBody: any = {
        id: id,
        name: values.name || "",
        brandId: values.brandId || "",
      };

      // Add branchId if available
      const branchId = values.branchId || selectedBranchId;
      if (branchId) {
        requestBody.branchId = branchId;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint, token),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage = "Запрашиваемый ресурс не найден.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      const result = await response.json();

      // Handle CRM API response format
      if (result.success && result.data) {
        modelsQuery.refetch();
        toast.success("Модель успешно обновлена");
        return response;
      }

      modelsQuery.refetch();
      toast.success("Модель успешно обновлена");
      return response;
    } catch (error) {
      const message = (error as any)?.message || "Ошибка при обновлении модели";
      toast.error(message);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteModel = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Construct endpoint with branchId query parameter
      let endpoint = API_ENDPOINTS.MODELS.BY_ID(id);
      if (selectedBranchId) {
        endpoint += `?branchId=${selectedBranchId}`;
      }

      const url = buildApiUrl(endpoint);
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint, token),
        },
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage =
            "Модель не найдена. Возможно, она уже удалена или принадлежит другому филиалу.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      const result = await response.json();

      // Handle CRM API response format
      if (result.success && result.data) {
        modelsQuery.refetch();
        toast.success("Модель успешно удалена");
        return true;
      }

      modelsQuery.refetch();
      toast.success("Модель успешно удалена");
      return true;
    } catch (error) {
      const message = (error as any)?.message || "Ошибка при удалении модели";
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch brands on mount and when branch changes
  useEffect(() => {
    fetchBrands();
  }, [selectedBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    models,
    brands,
    isLoading:
      isLoading ||
      modelsQuery.isLoading ||
      modelsQuery.isFetching ||
      brandsQuery.isLoading ||
      brandsQuery.isFetching ||
      createMutation.isPending,
    totalItems,
    fetchModels,
    fetchBrands,
    handleCreateModel,
    handleUpdateModel,
    handleDeleteModel,
    // Pagination handlers
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalPages: pagination.totalPages,
    handlePageChange: pagination.handlePageChange,
    handleItemsPerPageChange: pagination.handleItemsPerPageChange,
  };
};
